import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Mail, Phone, Printer, MapPin } from "lucide-react";
import { useSEO, BASE } from "@/lib/seo";
import { METIERS } from "@/lib/partenaires";
import ArticleShell, { P, SeeAlso } from "@/components/site/ArticleShell";
import { Reveal } from "@/components/site/Reveal";
import { IlluFigure } from "@/components/site/IlluFigure";
import { PARTENAIRE_ILLUS } from "@/lib/illustrations";
import NotFound from "@/pages/NotFound";

const PartnerCard = ({ partner: p, metier }) => {
  if (p.detailPage) {
    return (
      <article
        className="mt-14 border border-ink/15 p-8 sm:p-10"
        data-testid={`partner-card-${p.id}`}
      >
        <p className="eyebrow">{p.role}</p>
        <div className="mt-4 flex items-center gap-5">
          {p.logoCard && (
            <img
              src={p.logoCard.src}
              alt={p.logoCard.alt}
              loading="lazy"
              className="w-20 shrink-0 border border-ink/10 bg-ink"
              data-testid={`partner-card-logo-${p.id}`}
            />
          )}
          <h2 className="font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
            {p.name}
          </h2>
        </div>
        <P>{p.blurb}</P>
        <Link
          to={`/partenaires/${metier.slug}/${p.id}`}
          data-testid={`partner-fiche-${p.id}`}
          className="group mt-6 inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
        >
          <span className="link-underline">Découvrir la fiche complète</span>
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </article>
    );
  }
  return (<article
    className="mt-14 border border-ink/15 p-8 sm:p-10"
    data-testid={`partner-card-${p.id}`}
  >
    <p className="eyebrow">{p.role}</p>
    <div className="mt-4 flex items-center gap-5">
      {p.logoCard && (
        <img
          src={p.logoCard.src}
          alt={p.logoCard.alt}
          loading="lazy"
          className="w-20 shrink-0 border border-ink/10 bg-ink"
          data-testid={`partner-card-logo-${p.id}`}
        />
      )}
      <h2 className="font-serif text-3xl font-normal leading-snug tracking-tight text-ink sm:text-4xl">
        {p.name}
      </h2>
    </div>

    {p.photo && (
      <Reveal className="mt-8">
        <figure>
          <div className="img-content-wrap">
            <img
              src={p.photo.src}
              alt={p.photo.alt}
              className="img-content border border-ink/10"
              loading="lazy"
            />
          </div>
          <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.18em] text-smoke">
            {p.photo.caption}
          </figcaption>
          {p.photo.link && (
            <a
              href={p.photo.link}
              target="_blank"
              rel="noopener"
              data-testid="partenaire-photo-lien"
              className="group mt-3 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-bronze"
            >
              <span className="link-underline">{p.photo.linkLabel}</span>
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </figure>
      </Reveal>
    )}

    {p.paragraphs.map((text, i) => (
      <P key={i}>{text}</P>
    ))}

    {metier.slug === "ferronnerie" && PARTENAIRE_ILLUS[metier.slug] && (
      <div className="mt-10" data-testid="partenaire-gestes">
        <p className="eyebrow">Les gestes du métier</p>
        <div className="mt-6 grid gap-10 sm:grid-cols-3">
          {PARTENAIRE_ILLUS[metier.slug].map((entry, i) => (
            <IlluFigure key={entry.src} entry={entry} testId={`partenaire-geste-${i}`} />
          ))}
        </div>
      </div>
    )}

    <div className="mt-8 border border-ink/15 px-6 py-5">
      <p className="text-base font-normal leading-relaxed text-ink sm:text-lg" data-testid={`partner-cta-${p.id}`}>
        {p.cta.split(p.contact.tel)[0]}
        <a href={`tel:${p.contact.telHref}`} className="font-medium text-bronze hover:underline">
          {p.contact.tel}
        </a>
        {p.cta.split(p.contact.tel)[1] || ""}
      </p>
    </div>

    <address
      className="mt-8 border-t border-ink/10 pt-6 text-sm font-light not-italic leading-relaxed text-body"
      data-testid={`partner-contact-${p.id}`}
    >
      <p className="font-medium text-ink">{p.contact.company}</p>
      {p.contact.addressLines.map((line, i) => (
        <p key={i} className="flex items-start gap-2">
          {i === 0 && <MapPin className="mt-1 h-3.5 w-3.5 shrink-0 text-bronze" />}
          <span className={i > 0 ? "pl-[22px]" : ""}>{line}</span>
        </p>
      ))}
      <p className="mt-2 flex items-center gap-2">
        <Phone className="h-3.5 w-3.5 shrink-0 text-bronze" />
        <a href={`tel:${p.contact.telHref}`} className="text-bronze hover:underline">
          {p.contact.tel}
        </a>
        {p.contact.fax && (
          <span className="inline-flex items-center gap-2 pl-2 text-smoke">
            <Printer className="h-3.5 w-3.5" /> {p.contact.fax}
          </span>
        )}
      </p>
      <p className="mt-1 flex items-center gap-2">
        <Mail className="h-3.5 w-3.5 shrink-0 text-bronze" />
        <a href={`mailto:${p.contact.email}`} className="text-bronze hover:underline">
          {p.contact.email}
        </a>
      </p>
    </address>

    <Link
      to={`/materiaux-et-techniques/${metier.materiauxSlug}`}
      data-testid={`partner-materiau-${p.id}`}
      className="group mt-8 inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
    >
      <span className="link-underline">En savoir plus sur la {metier.name.toLowerCase()}</span>
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </Link>
  </article>
  );
};

export default function PartenaireMetierPage() {
  const { slug } = useParams();
  const metier = METIERS.find((m) => m.slug === slug);

  useSEO(
    metier ? `${metier.title.charAt(0).toUpperCase()}${metier.title.slice(1)} | Sculpteur Français` : "Partenaires — Sculpteur Français",
    metier
      ? `${metier.title} : ${metier.partners.map((p) => `${p.name} (${p.contact.company})`).join(", ")} — savoir-faire, réalisations et coordonnées.`
      : "Nos partenaires artisans des métiers d'art de la sculpture.",
    metier
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: metier.title,
          inLanguage: "fr-FR",
          url: `${BASE}/partenaires/${metier.slug}`,
          itemListElement: metier.partners.map((p, i) => ({
            "@type": "ListItem",
            position: i + 1,
            item: {
              "@type": "Organization",
              name: p.contact.company,
              description: `${p.name} — ${p.role}`,
              telephone: p.contact.tel,
              email: p.contact.email,
              address: { "@type": "PostalAddress", streetAddress: p.contact.addressLines[0], addressCountry: metier.slug === "resine" ? "PT" : "FR" },
            },
          })),
        }
      : null,
    metier
      ? {
          type: "article",
          breadcrumb: [
            { name: "Partenaires", path: "/partenaires-metiers-d-art" },
            { name: metier.name, path: `/partenaires/${metier.slug}` },
          ],
        }
      : { noindex: true }
  );

  if (!metier) return <NotFound />;

  return (
    <ArticleShell
      testId={`partenaires-${metier.slug}-page`}
      eyebrow={`Partenaires — ${metier.name}`}
      titleLines={[metier.title]}
      lead={metier.lead}
    >
      <Link
        to="/partenaires-metiers-d-art"
        data-testid="partenaires-back-link"
        className="group inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
      >
        <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
        <span className="link-underline">Tous les métiers partenaires</span>
      </Link>

      {metier.partners.map((p) => (
        <PartnerCard key={p.id} partner={p} metier={metier} />
      ))}

      <div className="mt-12">
        <Link
          to="/devenez-partenaire-metiers-d-art"
          data-testid="metier-devenir-btn"
          className="group inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
        >
          Inscription gratuite à l'annuaire
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </div>

      <SeeAlso
        links={[
          { to: "/partenaires-metiers-d-art", label: "Tous les métiers partenaires" },
          ...(metier.materiauxSlug
            ? [{ to: `/materiaux-et-techniques/${metier.materiauxSlug}`, label: `La fiche matériau : ${metier.name.toLowerCase()}` }]
            : [{ to: "/devenez-partenaire-metiers-d-art", label: "Inscription gratuite à l'annuaire" }]),
        ]}
      />
    </ArticleShell>
  );
}
